import { isWeb, type TransitionProp } from "tamagui";

// Variants cannot carry `transition`; components attach the spring themselves.
export type Press = "default" | "strong" | "none";
export type Appear = "fade" | "rise" | "scale" | "drop" | "statement";

export const pressVariants = {
  default: { pressStyle: { scale: 0.97 } },
  strong: { pressStyle: { scale: 0.94 } },
  none: {},
} as const satisfies Record<Press, object>;

// Web wants CSS property names; native wants shorthands expanded.
const nativeExpansions: Record<string, string[]> = {
  borderColor: [
    "borderTopColor",
    "borderRightColor",
    "borderBottomColor",
    "borderLeftColor",
  ],
};
export const animateOnly = (...keys: string[]) =>
  isWeb
    ? keys.map(key => key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`))
    : keys.flatMap(key => nativeExpansions[key] ?? [key]);

// `opacity: 1` pins the exit start; see the opacity gotcha in references/tamagui.md.
export const appearVariants = {
  fade: { opacity: 1, enterStyle: { opacity: 0 }, exitStyle: { opacity: 0 } },
  rise: {
    opacity: 1,
    enterStyle: { opacity: 0, y: 8 },
    exitStyle: { opacity: 0, y: 8 },
  },
  scale: {
    opacity: 1,
    enterStyle: { opacity: 0, scale: 0.96 },
    exitStyle: { opacity: 0, scale: 0.96 },
  },
  drop: {
    opacity: 1,
    enterStyle: { opacity: 0, y: -8, scale: 0.96 },
    exitStyle: { opacity: 0, y: -8, scale: 0.96 },
  },
  statement: {
    opacity: 1,
    enterStyle: { opacity: 0, y: 16, scale: 0.9 },
    exitStyle: { opacity: 0, y: 8 },
  },
} as const satisfies Record<Appear, object>;

// Native needs a resting key or it freezes at enterStyle; web must not have
// one or every at-rest style change springs.
const restingKey = isWeb ? {} : { default: "enter" as const };
export const presenceTransition = {
  ...restingKey,
  enter: "enter",
  exit: "exit",
  opacity: "fade",
} as const;

// Spread, not a prop: `transition={undefined}` still counts as animated.
export const motionTransition = (
  press: Press | undefined,
  appear?: Appear,
): { transition?: TransitionProp } => {
  const pressing = press !== undefined && press !== "none";
  if (appear === undefined) {
    return pressing ? { transition: "press" } : {};
  }
  const enter = appear === "statement" ? "expressive" : "enter";
  return {
    transition: {
      ...presenceTransition,
      ...(pressing ? { default: "press" } : isWeb ? {} : { default: enter }),
      enter,
    },
  };
};

/** Enter/exit props for a direct child of `AnimatePresence`; spread onto any element. */
export const appear = (shape: Appear) => ({
  ...appearVariants[shape],
  ...motionTransition(undefined, shape),
});
